import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Eye, Home, Edit } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("interviews");
  const [stats, setStats] = useState({});
  const [interviews, setInterviews] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    question_text: "",
    category: "",
    time_limit: 60,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, interviewsRes, questionsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/interviews`),
        axios.get(`${API}/questions`),
      ]);
      setStats(statsRes.data);
      setInterviews(interviewsRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      if (editingQuestion) {
        // Update existing question
        await axios.put(`${API}/questions/${editingQuestion.id}`, newQuestion);
      } else {
        // Add new question
        await axios.post(`${API}/questions`, newQuestion);
      }
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setNewQuestion({ question_text: "", category: "", time_limit: 60 });
      fetchDashboardData();
    } catch (error) {
      console.error("Error saving question:", error);
      alert("Gagal menyimpan pertanyaan");
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setNewQuestion({
      question_text: question.question_text,
      category: question.category,
      time_limit: question.time_limit,
    });
    setShowQuestionForm(true);
  };

  const handleCancelEdit = () => {
    setShowQuestionForm(false);
    setEditingQuestion(null);
    setNewQuestion({ question_text: "", category: "", time_limit: 60 });
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try {
      await axios.delete(`${API}/questions/${questionId}`);
      fetchDashboardData();
    } catch (error) {
      console.error("Error deleting question:", error);
    }
  };

  const viewResults = (interviewId) => {
    navigate(`/results/${interviewId}`);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" data-testid="admin-dashboard">
      <div className="dashboard-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ 
              fontSize: "0.9rem", 
              fontWeight: "700", 
              color: "#0891b2", 
              marginBottom: "0.5rem",
              letterSpacing: "1.5px"
            }}>
              RASPRO UNIVERSE
            </div>
            <h1 className="dashboard-title" data-testid="dashboard-title">Dashboard Admin</h1>
            <p style={{ color: "#64748b", marginTop: "0.5rem" }}>
              Kelola interview dan pertanyaan
            </p>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
          >
            <Home size={20} />
            Kembali ke Beranda
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card" data-testid="total-interviews-stat">
          <div className="stat-value">{stats.total_interviews || 0}</div>
          <div className="stat-label">Total Interview</div>
        </div>
        <div className="stat-card" data-testid="completed-interviews-stat">
          <div className="stat-value">{stats.completed_interviews || 0}</div>
          <div className="stat-label">Selesai</div>
        </div>
        <div className="stat-card" data-testid="in-progress-interviews-stat">
          <div className="stat-value">{stats.in_progress_interviews || 0}</div>
          <div className="stat-label">Sedang Berlangsung</div>
        </div>
        <div className="stat-card" data-testid="total-questions-stat">
          <div className="stat-value">{stats.total_questions || 0}</div>
          <div className="stat-label">Total Pertanyaan</div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tab-buttons">
          <button
            className={`tab-btn ${activeTab === "interviews" ? "active" : ""}`}
            onClick={() => setActiveTab("interviews")}
            data-testid="interviews-tab"
          >
            Interview
          </button>
          <button
            className={`tab-btn ${activeTab === "questions" ? "active" : ""}`}
            onClick={() => setActiveTab("questions")}
            data-testid="questions-tab"
          >
            Pertanyaan
          </button>
        </div>

        {activeTab === "interviews" && (
          <div data-testid="interviews-content">
            <h2 style={{ fontSize: "1.5rem", marginBottom: "1.5rem" }}>
              Semua Interview
            </h2>
            {interviews.length === 0 ? (
              <p style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>
                Belum ada interview
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Kandidat</th>
                    <th>Email</th>
                    <th>Posisi</th>
                    <th>Status</th>
                    <th>Dimulai Pada</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {interviews.map((interview) => (
                    <tr key={interview.id}>
                      <td>{interview.candidate_name}</td>
                      <td>{interview.candidate_email}</td>
                      <td>{interview.position}</td>
                      <td>
                        <span
                          className={`status-badge ${
                            interview.status === "completed"
                              ? "status-completed"
                              : "status-in-progress"
                          }`}
                        >
                          {interview.status === "completed" ? "Selesai" : "Berlangsung"}
                        </span>
                      </td>
                      <td>
                        {new Date(interview.started_at).toLocaleDateString('id-ID')}
                      </td>
                      <td>
                        {interview.status === "completed" && (
                          <button
                            className="btn btn-primary"
                            style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
                            onClick={() => viewResults(interview.id)}
                            data-testid={`view-results-btn-${interview.id}`}
                          >
                            <Eye size={16} />
                            Lihat Hasil
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "questions" && (
          <div data-testid="questions-content">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 style={{ fontSize: "1.5rem" }}>Pertanyaan Interview</h2>
              <button
                className="btn btn-primary"
                onClick={() => setShowQuestionForm(true)}
                data-testid="add-question-btn"
              >
                <Plus size={20} />
                Tambah Pertanyaan
              </button>
            </div>

            {questions.length === 0 ? (
              <p style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>
                Belum ada pertanyaan. Tambahkan pertanyaan pertama Anda!
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pertanyaan</th>
                    <th>Kategori</th>
                    <th>Batas Waktu</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question) => (
                    <tr key={question.id}>
                      <td>{question.question_text}</td>
                      <td>
                        <span
                          style={{
                            background: "#e0f2fe",
                            color: "#0891b2",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "20px",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          {question.category}
                        </span>
                      </td>
                      <td>{question.time_limit}d</td>
                      <td>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            className="btn btn-primary"
                            style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
                            onClick={() => handleEditQuestion(question)}
                            data-testid={`edit-question-btn-${question.id}`}
                          >
                            <Edit size={16} />
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
                            onClick={() => handleDeleteQuestion(question.id)}
                            data-testid={`delete-question-btn-${question.id}`}
                          >
                            <Trash2 size={16} />
                            Hapus
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showQuestionForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "2rem",
          }}
          data-testid="question-form-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowQuestionForm(false);
          }}
        >
          <div
            style={{
              background: "white",
              padding: "2.5rem",
              borderRadius: "20px",
              maxWidth: "600px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.8rem", marginBottom: "1.5rem", color: "#0891b2" }}>
              {editingQuestion ? "Edit Pertanyaan" : "Tambah Pertanyaan Baru"}
            </h2>
            <form onSubmit={handleAddQuestion}>
              <div className="form-group">
                <label className="form-label">Teks Pertanyaan</label>
                <textarea
                  className="form-textarea"
                  placeholder="Ceritakan tentang pengalaman Anda dengan..."
                  value={newQuestion.question_text}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, question_text: e.target.value })
                  }
                  required
                  data-testid="question-text-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Kategori</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Teknis, Perilaku, dll."
                  value={newQuestion.category}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, category: e.target.value })
                  }
                  required
                  data-testid="category-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Batas Waktu (detik)</label>
                <input
                  type="number"
                  className="form-input"
                  value={newQuestion.time_limit}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, time_limit: parseInt(e.target.value) })
                  }
                  min="30"
                  max="300"
                  required
                  data-testid="time-limit-input"
                />
              </div>
              <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  data-testid="submit-question-btn"
                >
                  {editingQuestion ? "Simpan Perubahan" : "Tambah Pertanyaan"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelEdit}
                  data-testid="cancel-question-btn"
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
